/**
 * usePortraitRequest - Debounced WebSocket portrait request hook
 *
 * Extracted from CharDetailsPanel to keep file size under 600 lines.
 * Only active in lobby context — requests a character portrait via
 * WebSocket if one isn't already stored.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLobbyStore, useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { websocketService } from '@/lib/websocket-service';

export function usePortraitRequest(context: 'lobby' | 'game-session' = 'lobby') {
  const isLobby = context === 'lobby';
  const selectedCharacter = useSelectedCharacter();
  const characterPortraitUrl = useLobbyStore((state) => state.characterPortraitUrl);
  const characterPortraitType = useLobbyStore((state) => state.characterPortraitType);
  const isGeneratingPortrait = useLobbyStore((state) => state.isGeneratingPortrait);
  const isOnline = useLobbyStore((state) => state.isOnline);

  const lastRequestedCharIdRef = useRef<string | null>(null);
  const portraitRequestTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get stored portrait URL from character session data (if previously generated)
  const storedPortraitUrl = (selectedCharacter?.session?.portrait_url ??
    selectedCharacter?.session?.portraitUrl) as string | undefined;

  // Use stored portrait if available, otherwise use dynamically generated one
  const effectivePortraitUrl = characterPortraitUrl || storedPortraitUrl || null;

  const enqueuePortraitRequest = useCallback(
    (characterId: string) => {
      if (!websocketService.isConnected()) return;

      if (portraitRequestTimeoutRef.current) {
        clearTimeout(portraitRequestTimeoutRef.current);
      }

      portraitRequestTimeoutRef.current = setTimeout(() => {
        if (selectedCharacter?.id !== characterId) return;
        lastRequestedCharIdRef.current = characterId;
        websocketService.requestPortrait(characterId);
        portraitRequestTimeoutRef.current = null;
      }, 400);
    },
    [selectedCharacter?.id]
  );

  // Request portrait when character is selected, we're connected, and we don't have one yet
  useEffect(() => {
    if (!isLobby) return;
    if (
      isOnline &&
      selectedCharacter?.id &&
      !effectivePortraitUrl &&
      selectedCharacter.id !== lastRequestedCharIdRef.current
    ) {
      enqueuePortraitRequest(selectedCharacter.id);
    }
  }, [isLobby, isOnline, selectedCharacter?.id, effectivePortraitUrl, enqueuePortraitRequest]);

  // Reset portrait when character changes
  useEffect(() => {
    if (!isLobby) return;
    if (selectedCharacter?.id !== lastRequestedCharIdRef.current) {
      if (portraitRequestTimeoutRef.current) {
        clearTimeout(portraitRequestTimeoutRef.current);
        portraitRequestTimeoutRef.current = null;
      }
      lastRequestedCharIdRef.current = null;
      useLobbyStore.getState().setCharacterPortrait(null, null);
    }
  }, [isLobby, selectedCharacter?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (portraitRequestTimeoutRef.current) {
        clearTimeout(portraitRequestTimeoutRef.current);
      }
    };
  }, []);

  return {
    effectivePortraitUrl,
    characterPortraitType,
    isGeneratingPortrait,
  };
}
